import { useCallback, useState } from 'react'

/**
 * Letter state shared by both referral forms: which target (draftKey) the letter was drafted
 * for, whether the clinician has edited it since, and the review attestation. The attestation
 * belongs to one draft target, one set of visit findings and one destination (office and fax
 * number) — it resets whenever any of them changes, so a letter reviewed for one recipient,
 * specialty, set of findings or fax destination can never be submitted for another. A new
 * destination alone does not redraft the letter.
 */
export function useReferralLetterState(draftKey: string | null, findings = '', destination = '') {
  const [reviewed, setReviewed] = useState(false)
  const [draftedFor, setDraftedFor] = useState<string | null>(null)
  const [letterEdited, setLetterEdited] = useState(false)
  const [reviewedKey, setReviewedKey] = useState(draftKey)
  const [reviewedFindings, setReviewedFindings] = useState(findings)
  const [reviewedDestination, setReviewedDestination] = useState(destination)
  // An AI draft is replacing the letter: until it lands the fields hold the template text.
  const [drafting, setDrafting] = useState(false)

  // Reset during render (not in an effect) so no render ever pairs the new target or findings
  // with the old attestation.
  if (
    draftKey !== reviewedKey ||
    findings !== reviewedFindings ||
    destination !== reviewedDestination
  ) {
    setReviewedKey(draftKey)
    setReviewedFindings(findings)
    setReviewedDestination(destination)
    setReviewed(false)
  }

  const onDrafted = useCallback((key: string) => {
    setDraftedFor(key)
    setLetterEdited(false)
    setReviewed(false)
  }, [])

  const onUserEdited = useCallback(() => {
    setLetterEdited(true)
    setReviewed(false)
  }, [])

  return {
    reviewed,
    setReviewed,
    drafting,
    letterProps: {
      draftKey,
      draftedFor,
      onDrafted,
      userEdited: letterEdited,
      onUserEdited,
      onDraftingChange: setDrafting,
    },
  }
}
